/**
 * Product identity — the title shown when the active route names no page.
 *
 * MUST stay equal to the `<title>` in `public/index.html` (a unit test pins the two together):
 * nginx serves that shell unauthenticated for every path, so it is the only title a link-preview
 * crawler can read — it never boots the SPA. Keeping both on the product name means a shared
 * link can no longer advertise an unrelated page, which is the bug this replaced.
 */
export const PRODUCT_TITLE = "Eudonet Platform";
/**
 * Short brand suffix on a resolved title — deliberately shorter than `PRODUCT_TITLE` so that a
 * truncated tab loses the brand rather than the page name.
 */
const BRAND = "Eudonet";
const SEPARATOR = " · ";
/**
 * Builds the document title from the `meta.titleKey` of every matched route record, so a nested
 * page names itself AND its parent section
 * (`/communication/:id/statistics` → "Statistiques · Campagne · Eudonet").
 *
 * Segments are leaf-first: tabs, bookmark bars and chat clients truncate the END of the string,
 * so the most specific segment has to come first to survive.
 */
export function resolveDocumentTitle(matched, translate) {
    // A Set because insertion order is preserved and a child inheriting its parent's key must not
    // double the segment.
    const trail = new Set();
    for (const record of matched) {
        const titleKey = record.meta.titleKey;
        if (!titleKey) {
            continue;
        }
        const label = translate(titleKey);
        // vue-i18n echoes the key back when a translation is missing, and the locale bundles are
        // pulled from Tolgee at build time — so a key CAN be absent from a given build. Dropping the
        // segment keeps `campaigns.title` out of the tab, the bookmark and the pasted link.
        if (!label || label === titleKey) {
            continue;
        }
        trail.add(label);
    }
    if (trail.size === 0) {
        return PRODUCT_TITLE;
    }
    return [...trail].reverse().concat(BRAND).join(SEPARATOR);
}
